/**
 * Forge-library import (incident 4b — 2026-06-10).
 *
 * Maps the MAIN Forge's persisted data model (Setup + SavedBuild, see
 * src/types) into the tool pages' Profile model (BowProfile + ArrowProfile,
 * see ./types) for READ-ONLY display on My Bows / My Arrows.
 *
 * Why two models exist: the main Forge persists Setups (1 bow + 1 sight + N
 * arrow BuildStates) and a flat SavedBuild[] (arrow snapshots). The tool
 * pages have their own gear store — BowProfile / ArrowProfile — authored
 * before the Builder shipped. Customers who saved gear in the Builder/Sketch
 * never saw it on the tool pages because the stores are separate AND
 * origin-isolated (Cody, 2026-06-10).
 *
 * These mappers are PURE and fully unit-tested. They never write to local
 * profile storage — the UI surfaces imported items read-only with an opt-in
 * "Save to My Bows/Arrows" action, so nothing is silently merged or mutated.
 *
 * Field mappings were verified against src/types/index.ts (NOT guessed):
 *
 *   Setup.bow: BowSetup { drawWeight, drawLength, iboSpeed, brand?, model? }
 *   Setup.sight: SightSelection { sightIdx -> SIGHTS catalog, pinDiameter }
 *   BuildState { shaftIdx, spineKey, length, pointWeight, insertIdx,
 *                customInsertWeight?, nockIdx, vaneIdx, vaneCount, helicalDeg? }
 *   SavedBuild { id, savedAt, name, state: BuildState }
 *
 * BuildState stores catalog INDICES, not names, so we resolve them against
 * the same catalogs the main Forge uses (SHAFTS/NOCKS/VANES/INSERTS), with
 * bounds guards: an out-of-range index (catalog drift between app versions)
 * degrades to blank identity rather than throwing.
 */
import type { Setup, SavedBuild, BuildState } from '@/types';
import { SHAFTS } from '@/data/shafts';
import { NOCKS } from '@/data/nocks';
import { VANES } from '@/data/vanes';
import { INSERTS } from '@/data/inserts';
import { SIGHTS } from '@/data/sights';
import type { BowProfile, ArrowProfile } from './types';

/** Prefix marking a profile as imported (read-only) from the main Forge. */
export const FORGE_IMPORT_PREFIX = 'forge-import:';

function at<T>(arr: readonly T[], idx: number | undefined): T | undefined {
  if (typeof idx !== 'number' || idx < 0 || idx >= arr.length) return undefined;
  return arr[idx];
}

function parseTimestamp(iso: string | undefined, fallback: number): number {
  if (!iso) return fallback;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : fallback;
}

/** Resolve a sight catalog index to a human "Brand Model" housing string. */
function sightHousing(sightIdx: number | undefined): string {
  const s = at(SIGHTS, sightIdx);
  if (!s) return '';
  return `${s.brand} ${s.model}`.trim();
}

/**
 * Map a main-Forge Setup to a (read-only) BowProfile. Bow + sight only —
 * the Setup's arrows are mapped separately via setupArrowsToProfiles().
 */
export function setupToBowProfile(setup: Setup): BowProfile {
  const created = parseTimestamp(setup.createdAt, Date.now());
  const updated = parseTimestamp(setup.updatedAt, created);
  return {
    profileId: `${FORGE_IMPORT_PREFIX}bow:${setup.id}`,
    name: setup.name || 'Forge setup',
    isPrimary: false,
    bow: {
      brand: setup.bow?.brand ?? '',
      model: setup.bow?.model ?? '',
      iboFps: setup.bow?.iboSpeed ?? 0,
      drawWeightLb: setup.bow?.drawWeight ?? 0,
      drawLengthIn: setup.bow?.drawLength ?? 0,
    },
    sight: {
      housing: sightHousing(setup.sight?.sightIdx),
      pinDiameterIn: setup.sight?.pinDiameter ?? 0.019,
    },
    createdAt: created,
    updatedAt: updated,
  };
}

/**
 * Map a BuildState (the main Forge's arrow snapshot) to a read-only
 * ArrowProfile. `idSuffix` makes the synthetic profileId stable + unique
 * across the source it came from (a SavedBuild id, or setupId#arrowIdx).
 */
export function buildStateToArrowProfile(
  state: BuildState,
  opts: { name?: string; idSuffix: string; createdAt?: number; updatedAt?: number },
): ArrowProfile {
  const shaft = at(SHAFTS, state.shaftIdx);
  const nock = at(NOCKS, state.nockIdx);
  const vane = at(VANES, state.vaneIdx);
  const insert = at(INSERTS, state.insertIdx);

  const insertWeight = insert?.isCustom
    ? (state.customInsertWeight ?? insert.weight)
    : (insert?.weight ?? 0);

  const created = opts.createdAt ?? Date.now();
  return {
    profileId: `${FORGE_IMPORT_PREFIX}arrow:${opts.idSuffix}`,
    name: opts.name || state.name || 'Forge build',
    isActive: false,
    shaft: {
      brand: shaft?.brand ?? '',
      model: shaft?.model ?? '',
      spine: state.spineKey,
      lengthIn: state.length,
    },
    nock: {
      type: nock?.name ?? 'Standard',
      weightGr: nock?.weight ?? 0,
    },
    vanes: {
      // VANES entries are a single "name" string (e.g. "AAE Max Hunter"); we
      // keep it in `model` rather than risk a bad brand/model split.
      model: vane?.name ?? '',
      count: state.vaneCount,
      offsetDeg: state.helicalDeg ?? 2,
    },
    insert: {
      // INSERTS entries are a single "name" string; surface it as brand so
      // the user can recognise it, plus the resolved weight.
      brand: insert?.name ?? '',
      weightGr: insertWeight,
    },
    point: {
      // BuildState carries pointWeight (authoritative) but no broadhead
      // identity we can trust to a catalog index, so default the type.
      type: 'COC fixed',
      weightGr: state.pointWeight,
    },
    createdAt: created,
    updatedAt: opts.updatedAt ?? created,
  };
}

/** Map every arrow in a Setup to read-only ArrowProfiles. */
export function setupArrowsToProfiles(setup: Setup): ArrowProfile[] {
  const created = parseTimestamp(setup.createdAt, Date.now());
  const updated = parseTimestamp(setup.updatedAt, created);
  return (setup.arrows ?? []).map((state, i) =>
    buildStateToArrowProfile(state, {
      name: state.name,
      idSuffix: `${setup.id}#${i}`,
      createdAt: created,
      updatedAt: updated,
    }),
  );
}

/** Map a flat SavedBuild to a read-only ArrowProfile. */
export function savedBuildToArrowProfile(b: SavedBuild): ArrowProfile {
  const ts = parseTimestamp(b.savedAt, Date.now());
  return buildStateToArrowProfile(b.state, {
    name: b.name,
    idSuffix: `saved:${b.id}`,
    createdAt: ts,
    updatedAt: ts,
  });
}

/* ------------------------- parsing of mirror blobs ------------------------- */

/** Parse the `sparrow_setups_v2` mirror blob. Tolerant of shape drift. */
export function parseForgeSetups(raw: string | null | undefined): Setup[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as { setups?: unknown } | unknown;
    const setups = (parsed as { setups?: unknown })?.setups ?? parsed;
    if (!Array.isArray(setups)) return [];
    return (setups as Setup[]).filter((s) => s && typeof s === 'object' && !!s.bow);
  } catch {
    return [];
  }
}

/** Parse the `sparrow_arrow_builds` mirror blob (a flat SavedBuild[]). */
export function parseForgeSavedBuilds(raw: string | null | undefined): SavedBuild[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return (parsed as SavedBuild[]).filter(
      (b) => b && typeof b === 'object' && !!b.state && typeof b.state === 'object',
    );
  } catch {
    return [];
  }
}

/* ------------------------- collection + dedupe ------------------------- */

/** Stable content signature for an arrow profile (for cross-source dedupe). */
function arrowSignature(a: ArrowProfile): string {
  return [
    a.name.trim().toLowerCase(),
    a.shaft.brand,
    a.shaft.model,
    a.shaft.spine,
    a.shaft.lengthIn,
    a.point.weightGr,
    a.insert.weightGr,
    a.nock.weightGr,
    a.vanes.model,
    a.vanes.count,
    a.vanes.offsetDeg,
  ].join('|');
}

/** Stable content signature for a bow profile (for cross-source dedupe). */
function bowSignature(b: BowProfile): string {
  return [
    b.name.trim().toLowerCase(),
    b.bow.brand,
    b.bow.model,
    b.bow.iboFps,
    b.bow.drawWeightLb,
    b.bow.drawLengthIn,
    b.sight.housing,
    b.sight.pinDiameterIn,
  ].join('|');
}

export interface ForgeLibrary {
  bows: BowProfile[];
  arrows: ArrowProfile[];
}

/**
 * Build the read-only imported library from the two mirror blobs. Arrows are
 * collected from BOTH the flat SavedBuild[] and every Setup's arrows, then
 * deduped by content signature (a build saved standalone and also living in a
 * setup shows once). Bows come from setups.
 */
export function collectForgeLibrary(
  setupsRaw: string | null | undefined,
  savedBuildsRaw: string | null | undefined,
): ForgeLibrary {
  const setups = parseForgeSetups(setupsRaw);
  const savedBuilds = parseForgeSavedBuilds(savedBuildsRaw);

  const bows: BowProfile[] = [];
  const seenBow = new Set<string>();
  for (const s of setups) {
    const bp = setupToBowProfile(s);
    const sig = bowSignature(bp);
    if (seenBow.has(sig)) continue;
    seenBow.add(sig);
    bows.push(bp);
  }

  const arrows: ArrowProfile[] = [];
  const seenArrow = new Set<string>();
  const pushArrow = (a: ArrowProfile) => {
    const sig = arrowSignature(a);
    if (seenArrow.has(sig)) return;
    seenArrow.add(sig);
    arrows.push(a);
  };
  // Standalone saved builds first (most explicit), then setup arrows.
  for (const b of savedBuilds) pushArrow(savedBuildToArrowProfile(b));
  for (const s of setups) for (const a of setupArrowsToProfiles(s)) pushArrow(a);

  // Newest first.
  bows.sort((a, b) => b.updatedAt - a.updatedAt);
  arrows.sort((a, b) => b.updatedAt - a.updatedAt);
  return { bows, arrows };
}
